import React, { useState } from 'react';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { TEXT_LABELS } from 'constants/productDetails';
import { Background, Main, Shade } from 'styles/themeColors';
import Button from 'components/UI/Button';
import RecentPromptsModal from './RecentPromptsModal';
import SamplePromptsModal from './SamplePromptsModal';
import { STAGE } from 'constants/aiVariantImport';
import { Space } from 'styles/themeSpaces';
import VariantIssueSelector from 'common/VariantIssueSelector';
import { VariantError } from 'data/ProductValidation';
import Product from 'data/Product';
import styled from 'styled-components';
import { getProductTypeConfig } from 'utils/product';
import Tooltip from 'components/UI/Tooltip';
import Icon from 'view/Icon';

const ClearAllFilters = styled.a`
  cursor: pointer;
  color: ${Main.brandDark};
`;

const InfoIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${Main.brandDark};
  transition: color 0.2s ease;

  &:hover {
    color: ${Main.black};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${Main.lightGray};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const StyledIcon = styled(Icon)<any>`
  color: ${Main.brandLight};
`;

interface Props {
  onApprove: () => void;
  onReject: () => void;
  isResLoading?: boolean;
  showModal: boolean;
  onClose: () => void;
  recentPrompts: string[];
  onSelectPrompt: (prompt: string) => void;
  handleModalOpen: () => void;
  stage: string;
  showActionButton: boolean;
  variantErrors: VariantError[] | undefined;
  productVariants: any[];
  product: Product;
  selectedIssue: VariantError[];
  setSelectedIssue: (issue: VariantError[]) => void;
  onAttributesEdit: () => void;
  disabled?: boolean;
  isDraftStatus?: boolean;
}

export default function SmartVariantsActionComponent({
  onApprove,
  onReject,
  isResLoading = false,
  showModal,
  onClose,
  recentPrompts,
  onSelectPrompt,
  handleModalOpen,
  stage,
  showActionButton,
  variantErrors,
  productVariants,
  product,
  selectedIssue,
  setSelectedIssue,
  onAttributesEdit,
  disabled,
  isDraftStatus = false,
}: Props) {
  
  const [showSamplePrompts, setShowSamplePrompts] = useState(false);

  let clearAllFiltersButton = null;

  if (selectedIssue.length) {
    clearAllFiltersButton = (
      <ClearAllFilters
        onClick={(e) => {
          e.preventDefault();
          setSelectedIssue([]);
        }}
      >
        {TEXT_LABELS.CLEAR_ALL_FILTERS}
      </ClearAllFilters>
    );
  }

  return (
    <>
      <FlexboxContainer
        width="100%"
        bgColor={Background.shaded}
        flexDirection="column"
        padding="40px 80px"
      >
        <RecentPromptsModal
          showModal={showModal}
          onClose={onClose}
          recentPrompts={recentPrompts}
          onSelectPrompt={onSelectPrompt}
        />
        <SamplePromptsModal
          showModal={showSamplePrompts}
          onClose={() => setShowSamplePrompts(false)}
          onSelectPrompt={onSelectPrompt}
        />

        <FlexboxContainer
          justifyContent="space-between"
          gap="12px"
          alignItems="flex-end"
        >
          {productVariants.length && variantErrors?.length ? (
            <FlexboxContainer
              alignItems="center"
              justifyContent="flex-start"
              margin="0 0 0 0"
            >
              <VariantIssueSelector
                className="issue-selector-container"
                errors={variantErrors!}
                onSelect={(error: VariantError[]) => {
                  setSelectedIssue(error);
                }}
                visible={variantErrors?.length > 0 || selectedIssue.length > 0}
                isSlbVendor={product.vendor?.isSlbVendor}
                productRedesignFlag={true}
              />
              {selectedIssue.length > 0 && clearAllFiltersButton}
            </FlexboxContainer>
          ) : null}

          <FlexboxContainer
            gap="12px"
            alignItems="flex-end"
            margin="0 0 0 auto"
          >
            <Tooltip content="How to use.." direction='up'>
              <InfoIconButton
                onClick={() => setShowSamplePrompts(true)}
                disabled={stage === STAGE.PROCESSING}
              >
              <StyledIcon name="info" size={18} />
              </InfoIconButton>
            </Tooltip>
          
            <FlexboxContainer gap="4px" alignItems="center">
                <Button
                disabled={stage === STAGE.PROCESSING}
                onClick={handleModalOpen}
                bgColor={stage === STAGE.PROCESSING ? Main.lightGray : Main.black}
                borderColor={
                  stage === STAGE.PROCESSING ? Main.lightGray : Main.black
                }
                theme="accent"
              >
                {TEXT_LABELS.RECENT_PROMPTS}
              </Button>
            </FlexboxContainer>
          
            <Button
              margin="0 15px 0 0px"
              onClick={onAttributesEdit}
              isGhost
              color={Background.navy}
              borderColor={Shade.shade550}
              disabled={disabled}
              width="135px"
              {...(isDraftStatus
                ? { permissionCode: getProductTypeConfig(product.productType).permissions.editAttributes }
                : {})}
            >
              {TEXT_LABELS.EDIT_ATTRIBUTES}
            </Button>
          </FlexboxContainer>

          {showActionButton && (
            <FlexboxContainer gap="12px" alignItems="flex-end">
              <Button
                isGhost
                theme="error"
                color={Main.error}
                margin={`0 ${Space.spHalf}`}
                onClick={onReject}
              >
                {TEXT_LABELS.REJECT}
              </Button>
              <Button
                onClick={onApprove}
                bgColor={Main.brandDark}
                borderColor={Main.brandDark}
                disabled={isResLoading}
                theme="accent"
              >
                {TEXT_LABELS.APPROVE}
              </Button>
            </FlexboxContainer>
          )}
        </FlexboxContainer>
      </FlexboxContainer>
    </>
  );
}
